import type {
  SecurityBadge,
  SecurityComplianceItem,
  SecurityControlItem,
  SecurityDeadlineItem,
  SecurityFooterLink,
  SecurityInfoSection,
  SecurityNavLink,
  SecurityQAItem,
  SecurityRecordItem,
} from "./types";

export const SECURITY_BACKGROUND_IMAGE =
  "https://framerusercontent.com/images/RBItp2feB3ps0n2iMCTeeKaGrBA.png?width=5760&height=3240";

export const SECURITY_NAV_LINKS: SecurityNavLink[] = [
  { label: "Home", href: "/" },
  { label: "Solutions", href: "/solutions" },
];

export const SECURITY_NAV_CTA = { label: "Join Waitlist", href: "/waitlist" };

export const SECURITY_INFO_SECTIONS: SecurityInfoSection[] = [
  {
    key: "sec-infra",
    eyebrow: "Infrastructure & network",
    title: "Defense-in-depth architecture",
    description:
      "Our infrastructure is built on AWS with defense-in-depth principles, ensuring isolation, encrypted communication, and robust network controls.",
    tone: "sky",
    gridClassName: "grid gap-4 md:grid-cols-2 lg:grid-cols-3",
    items: [
      {
        title: "Cloud infrastructure",
        desc: "AWS Fargate on ECS across isolated VPCs with no peering between them. Application and worker tasks run in private subnets with no public IPs.",
      },
      {
        title: "Network isolation",
        desc: "VPC with isolated public and private subnets across separate VPCs with no peering between them. Inbound traffic is filtered by AWS WAF with managed threat rules and rate limiting at the load balancer, and application tasks are never exposed directly to the public internet.",
      },
      {
        title: "Encrypted transport",
        desc: "HTTPS is enforced for every client connection, with all HTTP automatically redirected to HTTPS and TLS 1.2 or higher on every external connection.",
      },
      {
        title: "Container security",
        desc: "Amazon ECR with on-push image scanning enabled. Automated vulnerability detection for all container images.",
      },
      {
        title: "Auto scaling",
        desc: "Intelligent autoscaling policies for CPU and memory ensure optimal performance while maintaining security posture.",
      },
      {
        title: "Logging & monitoring",
        desc: "CloudWatch Logs with 365-day retention. Comprehensive audit trails and real-time monitoring of all infrastructure components.",
      },
    ],
  },
  {
    key: "sec-data",
    eyebrow: "Data protection",
    title: "Multi-layered encryption",
    description:
      "Multi-layered encryption protects your data at rest, in transit, and in use, following industry best practices and compliance standards.",
    tone: "green",
    gridClassName: "grid gap-4 md:grid-cols-2",
    items: [
      {
        title: "Data at rest",
        desc: "All production databases encrypted at rest with AWS managed encryption. Automated backups retained for 7 days with encryption protection.",
      },
      {
        title: "Data in transit",
        desc: "TLS 1.2+ enforced on all external client connections, HTTPS-only, with HSTS headers. Internal service traffic runs entirely within isolated private VPCs with no public exposure and no cross-VPC peering.",
      },
      {
        title: "Secret management",
        desc: "Application secrets and credentials are isolated in AWS Secrets Manager, and encryption keys are protected by AWS KMS, backed by FIPS 140-2 validated hardware security modules (HSMs).",
      },
      {
        title: "Database security",
        desc: "Production PostgreSQL databases run in private subnets and are not publicly accessible. Access is restricted to authorized ECS tasks and optional bastion or VPN connections, with credentials isolated in AWS Secrets Manager and never stored in application code or images.",
      },
    ],
  },
  {
    key: "ai-llm",
    eyebrow: "AI & LLM",
    title: "AI you can put on the record",
    description:
      "Where available, Zom shows supporting source information for review. Client data sent to the model provider is processed under zero-data-retention terms and is not used to train third-party models.",
    tone: "violet",
    gridClassName: "grid gap-4 md:grid-cols-2",
    items: [
      {
        title: "Model-provider data controls",
        desc: "Client data sent to the model provider is processed under zero-data-retention terms and is not used to train third-party models.",
      },
      {
        title: "Supporting source information",
        desc: "Where available, Zom shows supporting source information for review.",
      },
      {
        title: "Human review before any action",
        desc: "AI output is prepared for advisor review. Nothing is sent, traded, or acted on until a person approves it.",
      },
    ],
  },
  {
    key: "sec-appsec",
    eyebrow: "Application security",
    title: "Secure by design",
    description:
      "Secure by design with authentication, authorization, and input validation built into every layer of our application stack.",
    tone: "violet",
    gridClassName: "grid gap-4 md:grid-cols-2",
    items: [
      {
        title: "Authentication & authorization",
        desc: "JWT-based authentication with role-aware access control. API key validation for sensitive endpoints. Password hashing with bcrypt industry-standard algorithms.",
      },
      {
        title: "Input validation",
        desc: "Strongly typed configuration with required field validation. Environment variables validated at startup to prevent misconfiguration.",
      },
      {
        title: "Secret protection",
        desc: "Integration keys and sensitive credentials loaded from environment variables and AWS Secrets Manager. No hardcoded secrets in application code or images.",
      },
      {
        title: "Secure development",
        desc: "Environment files excluded from Docker builds. Secrets managed separately from application code. Type-safe configuration prevents common security pitfalls.",
      },
    ],
  },
  {
    key: "sec-cicd",
    eyebrow: "CI/CD & supply chain",
    title: "Secure deployment pipelines",
    description:
      "Secure deployment pipelines with automated scanning, scoped least-privilege AWS access, and controlled access to production environments.",
    tone: "amber",
    gridClassName: "grid gap-4 md:grid-cols-2",
    items: [
      {
        title: "Automated deployment",
        desc: "Deployments run through GitHub Actions with scoped, least-privilege AWS access and no long-lived credentials in application code. Every container image is tagged with its commit SHA for full traceability, and production releases are serialized to prevent overlapping deploys.",
      },
      {
        title: "Image scanning",
        desc: "ECR on-push scanning automatically detects vulnerabilities in container images. Deployment pipeline includes concurrency controls to prevent overlapping production deployments.",
      },
    ],
  },
  {
    key: "sec-ops",
    eyebrow: "Monitoring & operations",
    title: "Comprehensive observability",
    description:
      "Comprehensive monitoring, logging, and health checks ensure we can detect and respond to issues quickly.",
    tone: "cyan",
    gridClassName: "grid gap-4 md:grid-cols-2",
    items: [
      {
        title: "Centralized logging",
        desc: "CloudWatch Logs with 365-day retention for all application and infrastructure logs. Structured logging enables efficient analysis and alerting.",
      },
      {
        title: "Health monitoring",
        desc: "Application Load Balancer health checks ensure only healthy tasks receive traffic. Automated rollback on deployment failures.",
      },
    ],
  },
];

export const SECURITY_COMPLIANCE_ITEMS: SecurityComplianceItem[] = [
  {
    num: "01",
    title: "Least privilege",
    desc: "Access is limited to only those with legitimate business need and granted based on the principle of least privilege.",
  },
  {
    num: "02",
    title: "Defense in depth",
    desc: "Security controls are implemented and layered according to the principle of defense-in-depth across all systems.",
  },
  {
    num: "03",
    title: "Consistent controls",
    desc: "Security controls are applied consistently across all areas of the enterprise, ensuring uniform protection.",
  },
];

export const SECURITY_VDP_RULES = [
  "Follow responsible disclosure practices",
  "Do not access or modify user data without explicit permission",
  "Do not perform denial-of-service attacks or any testing that impacts availability",
  "Do not publicly disclose vulnerabilities until we have confirmed a fix or agreed on a timeline",
  'Only test systems and assets explicitly listed in the "In Scope" section',
  "Report vulnerabilities promptly after discovery",
] as const;

export const SECURITY_VDP_SCOPE_IN = [
  "ZOM production web application(s)",
  "ZOM public API endpoints",
] as const;

export const SECURITY_VDP_SCOPE_OUT = [
  "Denial-of-service (DoS/DDoS), load testing, or any activity that degrades availability",
  "Social engineering (phishing, vishing), employee targeting, or physical attacks",
  "Testing of third-party systems not owned or controlled by ZOM",
  "Automated scanning that materially impacts performance",
  "Physical security vulnerabilities",
  "Issues requiring access to physical devices or local network access",
] as const;

export const SECURITY_VDP_RESPONSE_COMMITMENT = [
  { step: "1", title: "Acknowledge receipt within 2 business days" },
  { step: "2", title: "Provide initial triage within 5 business days" },
  { step: "3", title: "Work toward remediation based on severity and risk" },
] as const;

export const SECURITY_VDP_REWARDS_CRITERIA = [
  "Severity of the vulnerability (Critical, High, Medium, Low)",
  "Quality and completeness of the vulnerability report",
  "Exploitability and potential impact",
  "Compliance with program rules and responsible disclosure practices",
] as const;

export const SECURITY_ADVISOR_CONTROLS: SecurityControlItem[] = [
  {
    title: "It does not send communications",
    desc: "Zom never auto-sends client emails or messages. It drafts them for review, and the advisor decides what goes out.",
  },
  {
    title: "It does not execute trades",
    desc: "Zom can surface drift, concentration, and tax-aware rebalancing considerations. It never places a trade.",
  },
  {
    title: "It does not move money",
    desc: "No transfer, disbursement, or money movement happens through Zom. Those actions stay with the advisor and the custodian.",
  },
  {
    title: "It does not replace fiduciary judgment",
    desc: "Zom prepares work product and documents activity. The advisor remains responsible for what is recommended, approved, and implemented.",
  },
];

export const SECURITY_COMPLIANCE_FRAMEWORKS = [
  "Fiduciary duty stays with the advisor",
  "SEC Marketing Rule",
  "Books and records (Rule 204-2)",
  "Reg S-P privacy and vendor oversight",
  "SEC examination focus on AI",
] as const;

export const SECURITY_SEC_QA: SecurityQAItem[] = [
  {
    q: "Does using AI violate my fiduciary duty?",
    a: "No. The advisor remains responsible for the advice, regardless of the tools used to generate it. Zom surfaces data and prepares work product. The advisor makes the final decision.",
  },
  {
    q: "What if the AI gives wrong information?",
    a: "Zom pulls from connected firm systems such as CRM, custodian, planning, portfolio, and compliance records. Human review remains required before action, and Zom keeps an audit trail of query and response history.",
  },
  {
    q: "Do we need to disclose that we use AI?",
    a: "If AI use is material to how the firm delivers services, it should be reflected accurately in Form ADV Part 2A and related disclosures. Do not overstate what the platform does or understate its use.",
  },
  {
    q: "What about Reg S-P?",
    a: "Zom supports privacy and vendor oversight expectations through access controls, encryption, model-provider data controls, and contractual breach-notification practices.",
  },
  {
    q: "What about recordkeeping under Rule 204-2?",
    a: "AI-generated output transmitted to a client or used in an investment recommendation should be retained. Zom is designed to maintain exportable audit trails of queries and responses.",
  },
  {
    q: "Can we use AI-generated content in client communications?",
    a: "Yes, if the content is reviewed for accuracy and approved before use. Zom does not auto-send client communications.",
  },
  {
    q: "What is the SEC examining in relation to AI?",
    a: "Core areas include AI usage matching disclosures, governance, cybersecurity, employee training, privacy controls, and books-and-records practices.",
  },
];

export const SECURITY_SEC_DEADLINES: SecurityDeadlineItem[] = [
  {
    when: "In effect",
    what: "SEC Marketing Rule and Reg S-P obligations for larger advisers.",
  },
  {
    when: "June 3, 2026",
    what: "Reg S-P amendments compliance deadline for smaller advisers.",
  },
  {
    when: "Ongoing",
    what: "SEC examination focus on AI usage, disclosures, governance, and cybersecurity.",
  },
];

export const SECURITY_RECORD_ITEMS: SecurityRecordItem[] = [
  {
    title: "Query and response history",
    desc: "A preserved record of what was asked and what the system returned, supporting books-and-records obligations.",
  },
  {
    title: "Advice and communications",
    desc: "A searchable record of advice, communications, AI outputs, and IPS alignment across the relationship.",
  },
  {
    title: "Supporting source information",
    desc: "Where available, Zom shows supporting source information for review.",
  },
  {
    title: "Advisor review documented",
    desc: "A clear record of what was reviewed, generated, approved, and acted on, with exception handling captured.",
  },
];

export const SECURITY_CTA_BADGES: SecurityBadge[] = [
  { label: "AWS Infrastructure", tone: "sky" },
  { label: "Encrypted at Rest", tone: "green" },
  {
    label: "Encrypted in Transit",
    tone: "green",
  },
  { label: "Secrets Management", tone: "violet" },
];

export const SECURITY_FOOTER_LINKS: SecurityFooterLink[] = [
  { label: "Home", href: "/" },
  { label: "Solutions", href: "/solutions" },
  { label: "Product", href: "/product" },
  { label: "Security", href: "/security" },
  { label: "Newsroom", href: "/newsroom" },
  { label: "Team", href: "/team" },
  { label: "FAQ", href: "/faq" },
  { label: "Contact", href: "/contact" },
  { label: "Waitlist", href: "/waitlist" },
  { label: "Privacy", href: "/privacy" },
  { label: "Terms", href: "/terms" },
];
